import api from '@api/apiServices';
import LtrProvider from '@Components/Common/LtrProvider';
import useCheckout from '@hooks/useCheckout';
import {
    AddRounded,
    BoltRounded,
    CloseRounded,
    CreditCardRounded,
    GppGoodOutlined,
    InfoOutlined,
    RemoveRounded,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    Stack,
    IconButton,
    Slider,
    Box,
    Button,
    Card,
    CardContent,
    ToggleButtonGroup,
    ToggleButton,
    alpha,
    Chip,
    CircularProgress,
    Divider,
    Skeleton,
} from '@mui/material';
import {
    initializePaddle,
    Paddle,
    PaddleEventData,
    Price as PaddlePrice,
    TransactionPreviewResponse,
} from '@paddle/paddle-js';
import {
    useMutation,
    useQuery,
    useQueryClient,
    useSuspenseQuery,
} from '@tanstack/react-query';
import { settingsQueryOptions } from '@utils/queryOptions';
import camelcaseKeys from 'camelcase-keys';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ManagePlan = ({ open, onClose }) => {
    const {
        t,
        i18n: { language: locale },
    } = useTranslation();
    const paperRef = useRef(null);
    const openCheckout = useCheckout({ onSuccess: onClose });
    const queryClient = useQueryClient();

    const prices = camelcaseKeys(queryClient.getQueryData(['prices']), {
        deep: true,
    });

    const monthlyPrices = prices.filter(
        p =>
            !p?.billingCycle?.interval.value ||
            p.billingCycle.interval.value === 'month',
    );
    const yearlyPrices = prices.filter(
        p =>
            !p?.billingCycle?.interval.value ||
            p.billingCycle.interval.value === 'year',
    );

    const [paddle, setPaddle] = useState<Paddle | null>(null);

    const { data: settings, refetch: refetchSettings } = useSuspenseQuery(
        settingsQueryOptions(),
    );

    const currentSubscription = camelcaseKeys(
        settings.email_plan.subscription_data,
        { deep: true },
    );

    const currentPrice = currentSubscription?.items?.[0]?.price;

    const isCurrentPlanFree = !currentPrice;

    const [isYearly, setIsYearly] = useState(
        settings.email_plan.billing_cycle === 'Yearly',
    );
    const periodPrices = isYearly ? yearlyPrices : monthlyPrices;

    const [selectedPriceIndex, setSelectedPriceIndex] = useState(
        Math.max(
            periodPrices.findIndex(p => p.id === settings.email_plan.price_id),
            0,
        ),
    );

    const newPlan = periodPrices[selectedPriceIndex];

    const isNewPlanFree = newPlan.unitPrice.amount === '0';

    const parseAmount = (amount?: string) =>
        amount ? parseInt(amount) / 100 : undefined;

    const isUpgrading =
        parseAmount(newPlan.unitPrice.amount) >
        parseAmount(currentPrice?.unitPrice.amount);

    const subscriptionUpdatePayload = {
        items: [
            {
                price_id: newPlan?.id,
                quantity: 1,
            },
        ],
        proration_billing_mode: isUpgrading
            ? 'prorated_immediately'
            : 'do_not_bill',
    };

    const { data: subscriptionUpdatePreview, isLoading: updatePreviewLoading } =
        useQuery({
            queryKey: ['subscription-update-preview', newPlan?.id],
            queryFn: () => {
                return api.settings.billing[
                    'preview-subscription-update'
                ].patch(undefined, subscriptionUpdatePayload);
            },
            enabled:
                !!paddle &&
                !isCurrentPlanFree &&
                !isNewPlanFree &&
                newPlan?.id !== currentPrice?.id,
        });

    const { data: newSubscriptionPreview, isLoading: newPreviewLoading } =
        useQuery({
            queryKey: ['new-subscription-preview', newPlan?.id],
            queryFn: () =>
                paddle
                    ?.TransactionPreview({
                        items: [
                            {
                                priceId: newPlan.id,
                                quantity: 1,
                            },
                        ],
                    })
                    .then(res => res.data),
            enabled: !!paddle && isCurrentPlanFree && !isNewPlanFree,
        });

    const previewLoading = newPreviewLoading || updatePreviewLoading;

    const freeToPaidPeriodCost = parseAmount(
        newSubscriptionPreview?.details?.totals?.grandTotal,
    );
    const freeToPaidCurrency =
        newSubscriptionPreview?.details?.totals?.currencyCode;

    const paidToSamePeriodCost = parseAmount(
        currentSubscription?.recurringTransactionDetails?.totals?.total,
    );
    const paidToSameCurrency =
        currentSubscription?.recurringTransactionDetails?.totals?.currencyCode
            ?.value;

    const paidToOtherPaidPeriodCost = parseAmount(
        subscriptionUpdatePreview?.recurring_transaction_details?.totals?.total,
    );
    const paidToOtherPaidCurrency =
        subscriptionUpdatePreview?.recurring_transaction_details?.totals
            ?.currency_code?.value;

    const getMonthlyCost = (periodCost: number) =>
        periodCost ? (isYearly ? periodCost / 12 : periodCost) : undefined;

    const handlePaddleEvents = (event: PaddleEventData) => {
        if (event.name === 'checkout.completed') {
            setTimeout(() => refetchSettings(), 3000);
            onClose();
        }
    };

    const handlePriceChange = (newIndex: number) => {
        setSelectedPriceIndex(newIndex);
    };

    useEffect(() => {
        initializePaddle({
            environment: import.meta.env.VITE_PADDLE_ENVIRONMENT,
            token: import.meta.env.VITE_PADDLE_TOKEN,
            eventCallback: handlePaddleEvents,
        }).then(setPaddle);
    }, []);

    const { mutate: updateSubscription, isPending: isUpdating } = useMutation({
        mutationFn: () =>
            api.settings.billing['update-subscription'].patch(undefined, {
                price_id: newPlan?.id,
            }),
        onSuccess: () => {
            if (isUpgrading) {
                queryClient.setQueryData(['settings'], old => {
                    const optomisticData = old;
                    optomisticData.email_plan.subscription_data =
                        subscriptionUpdatePreview;
                    optomisticData.emaail_plan.monthly_emails = parseInt(
                        subscriptionUpdatePreview.items.price.custom_data.data
                            .monthly_emails,
                    );
                    onClose();

                    return optomisticData;
                });
            } else {
                queryClient.setQueryData(['settings'], old => {
                    const optomisticData = old;
                    optomisticData.email_plan.subscription_data.custom_data.data.next_plan =
                        subscriptionUpdatePreview.items[0].price;
                    optomisticData.email_plan.subscription_data.scheduled_change =
                        {
                            action: {
                                name: 'Pause',
                                value: 'pause',
                            },
                            resume_at: null,
                            effective_at:
                                optomisticData.email_plan.subscription_data
                                    .current_billing_period.ends_at,
                        };
                    onClose();

                    return optomisticData;
                });
            }
        },
    });

    const { mutate: cancelSubscription, isPending: isCancelling } = useMutation(
        {
            mutationFn: () =>
                api.settings.billing['cancel-paddle-subscription'].post(),
            onSuccess: () => {
                queryClient.setQueryData(['settings'], old => {
                    const optomisticData = old;
                    optomisticData.email_plan.subscription_data.scheduled_change =
                        {
                            action: {
                                name: 'Cancel',
                                value: 'cancel',
                            },
                            resume_at: null,
                            effective_at:
                                optomisticData.email_plan.subscription_data
                                    .current_billing_period.ends_at,
                        };
                    onClose();
                    return optomisticData;
                });
            },
        },
    );

    const PLAN_TRANSITIONS = {
        FREE: {
            FREE: {
                type: 'FREE_TO_FREE',
                confirmText: t('Keep Plan'),
                handleConfirm: onClose,
                previewSource: null,
                periodCost: undefined,
                monthlyCost: undefined,
                currency: undefined,
                isCurrentPlan: true,
                formattedMonthlyCost: t('Free'),
            },
            PAID: {
                type: 'FREE_TO_PAID',
                confirmText: t('Checkout'),
                handleConfirm: () =>
                    openCheckout({
                        height: paperRef.current?.clientHeight,
                        priceId: newPlan.id,
                    }),
                previewSource: newSubscriptionPreview,
                periodCost: freeToPaidPeriodCost?.toCurrencyString(
                    locale,
                    freeToPaidCurrency,
                ),
                monthlyCost: getMonthlyCost(freeToPaidPeriodCost),
                currency: freeToPaidCurrency,
                isCurrentPlan: false,
                formattedMonthlyCost: getMonthlyCost(
                    freeToPaidPeriodCost,
                )?.toCurrencyString(locale, freeToPaidCurrency),
            },
        },
        PAID: {
            FREE: {
                type: 'PAID_TO_FREE',
                confirmText: t('Cancel'),
                handleConfirm: cancelSubscription,
                previewSource: null,
                periodCost: undefined,
                monthlyCost: undefined,
                currency: undefined,
                isCurrentPlan: false,
                formattedMonthlyCost: t('Free'),
                updateMessage: t('downgrade-plan-message'),
            },
            SAME: {
                type: 'PAID_SAME',
                confirmText: t('Keep Plan'),
                handleConfirm: onClose,
                previewSource: currentSubscription,
                periodCost: paidToSamePeriodCost?.toCurrencyString(
                    locale,
                    paidToSameCurrency,
                ),
                monthlyCost: getMonthlyCost(paidToSamePeriodCost),
                currency: paidToSameCurrency,
                isCurrentPlan: true,
                formattedMonthlyCost: getMonthlyCost(
                    paidToSamePeriodCost,
                )?.toCurrencyString(locale, paidToSameCurrency),
            },
            UPGRADE: {
                type: 'PAID_UPGRADE',
                confirmText: t('Confirm Upgrade'),
                handleConfirm: updateSubscription,
                previewSource: subscriptionUpdatePreview,
                periodCost: paidToOtherPaidPeriodCost?.toCurrencyString(
                    locale,
                    paidToOtherPaidCurrency,
                ),
                monthlyCost: getMonthlyCost(paidToOtherPaidPeriodCost),
                currency: paidToOtherPaidCurrency,
                isCurrentPlan: false,
                formattedMonthlyCost: getMonthlyCost(
                    paidToOtherPaidPeriodCost,
                )?.toCurrencyString(locale, paidToOtherPaidCurrency),
                // updateMessage: `You'll be charged ${parseAmount(subscriptionUpdatePreview?.immediate_transaction?.details?.totals?.grand_total)?.toCurrencyString(locale, subscriptionUpdatePreview?.immediate_transaction?.details?.totals?.currency_code?.value)} now, and the new price will apply from the next billing cycle.`,
                updateMessage: t('upgrade-plan-message', {
                    immediateTransaction: parseAmount(
                        subscriptionUpdatePreview?.immediate_transaction
                            ?.details?.totals?.grand_total,
                    )?.toCurrencyString(
                        locale,
                        subscriptionUpdatePreview?.immediate_transaction
                            ?.details?.totals?.currency_code?.value,
                    ),
                }),
            },
            DOWNGRADE: {
                type: 'PAID_DOWNGRADE',
                confirmText: t('Switch Plan'),
                handleConfirm: updateSubscription,
                previewSource: subscriptionUpdatePreview,
                periodCost: paidToOtherPaidPeriodCost?.toCurrencyString(
                    locale,
                    paidToOtherPaidCurrency,
                ),
                monthlyCost: getMonthlyCost(paidToOtherPaidPeriodCost),
                currency: paidToOtherPaidCurrency,
                isCurrentPlan: false,
                formattedMonthlyCost: getMonthlyCost(
                    paidToOtherPaidPeriodCost,
                )?.toCurrencyString(locale, paidToOtherPaidCurrency),
                updateMessage: t('downgrade-plan-message'),
            },
        },
    };

    const getCurrentTransition = () => {
        let current = currentSubscription ? 'PAID' : 'FREE';
        let newType;
        // FREE_TO_FREE ✅
        // FREE_TO_PAID
        // PAID_TO_FREE ✅
        // PAID_SAME ✅
        // PAID_UPGRADE
        // PAID_DOWNGRADE
        if (!newPlan?.id) {
            newType = 'FREE';
        } else if (newPlan?.id === currentPrice?.id) {
            newType = 'SAME';
        } else if (!currentSubscription) {
            newType = 'PAID';
        } else if (isUpgrading) {
            newType = 'UPGRADE';
        } else {
            newType = 'DOWNGRADE';
        }
        return PLAN_TRANSITIONS[current][newType];
    };

    const currentTransition = getCurrentTransition();

    const marks = [
        {
            value: 0,
            label: '50',
        },
        {
            value: 6,
            label: '20K',
        },
    ];

    return (
        <Dialog
            PaperProps={{
                sx: {
                    backgroundColor: 'surfaceContainerLow.main',
                    // minHeight: 'calc(100% - 64px)',
                },
                ref: paperRef,
            }}
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="xs"
        >
            <DialogTitle>{t('Choose Your Plan')}</DialogTitle>
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={theme => ({
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseRounded />
            </IconButton>
            <DialogContent sx={{ textAlign: 'center', p: 2, pb: 0 }}>
                <Stack spacing={3}>
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        mb={3}
                    >
                        <ToggleButtonGroup
                            value={isYearly}
                            exclusive
                            onChange={(_, newValue) => {
                                if (newValue !== null) setIsYearly(newValue);
                            }}
                            sx={{
                                bgcolor: 'surfaceContainerHigh.main',
                                p: 0.5,
                                borderRadius: 4,
                                gap: 0.5,
                                '& .MuiToggleButtonGroup-grouped': {
                                    border: 0,
                                    '&:not(:first-of-type)': {
                                        borderRadius: 3,
                                        borderLeft: 0,
                                        marginLeft: 0,
                                    },
                                    '&:first-of-type': {
                                        borderRadius: 3,
                                    },
                                },
                            }}
                        >
                            <ToggleButton
                                value={false}
                                sx={{
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: 3,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    transition: 'all 0.2s',
                                    color: 'text.primary',
                                    opacity: !isYearly ? 1 : 0.4,
                                    bgcolor: !isYearly
                                        ? 'surface.main'
                                        : 'transparent',
                                    boxShadow: !isYearly ? 0.5 : 'none',
                                    '&:hover': {
                                        bgcolor: !isYearly
                                            ? 'surface.main'
                                            : 'transparent',
                                    },
                                    '&.Mui-selected': {
                                        bgcolor: 'surface.main',
                                        color: 'text.primary',
                                        boxShadow: 0.5,
                                        '&:hover': {
                                            bgcolor: 'surface.main',
                                        },
                                    },
                                }}
                            >
                                {t('Monthly')}
                            </ToggleButton>
                            <ToggleButton
                                value={true}
                                sx={{
                                    px: 2,
                                    py: 0.75,
                                    borderRadius: 3,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    transition: 'all 0.2s',
                                    color: 'text.primary',
                                    opacity: isYearly ? 1 : 0.4,
                                    bgcolor: isYearly
                                        ? 'surface.main'
                                        : 'transparent',
                                    boxShadow: isYearly ? 0.5 : 'none',
                                    '&:hover': {
                                        bgcolor: isYearly
                                            ? 'surface.main'
                                            : 'transparent',
                                    },
                                    '&.Mui-selected': {
                                        bgcolor: 'surface.main',
                                        color: 'text.primary',
                                        boxShadow: 0.5,
                                        '&:hover': {
                                            bgcolor: 'surface.main',
                                        },
                                    },
                                }}
                            >
                                {t('Yearly')}
                                <Box
                                    component="span"
                                    sx={{
                                        ml: 1,
                                        fontSize: '9px',
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: '6px',
                                        bgcolor: theme =>
                                            alpha(
                                                theme.palette.success.main,
                                                0.2,
                                            ),
                                        color: 'success.main',
                                    }}
                                >
                                    -20%
                                </Box>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box>
                        <Typography
                            variant="labelLarge"
                            color="GrayText"
                            fontWeight={900}
                            textTransform="uppercase"
                        >
                            {t('Select Monthly Volume')}
                        </Typography>
                        <LtrProvider>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="center"
                                spacing={3}
                                mt={2}
                            >
                                <IconButton
                                    onClick={() =>
                                        handlePriceChange(
                                            Math.max(0, selectedPriceIndex - 1),
                                        )
                                    }
                                    disabled={selectedPriceIndex <= 0}
                                >
                                    <Button variant="tonal" color="surface">
                                        <RemoveRounded />
                                    </Button>
                                </IconButton>
                                <Typography
                                    variant="displayLarge"
                                    component="span"
                                    fontWeight={900}
                                    sx={{
                                        width: '8rem',
                                        textAlign: 'center',
                                        color: 'primary.main',
                                    }}
                                >
                                    {newPlan.customData.data.monthlyEmails
                                        .toInt()
                                        .toShorthand()}
                                </Typography>
                                <IconButton
                                    onClick={() =>
                                        handlePriceChange(
                                            selectedPriceIndex + 1,
                                        )
                                    }
                                    disabled={
                                        selectedPriceIndex >=
                                        periodPrices.length - 1
                                    }
                                >
                                    <Button variant="tonal" color="surface">
                                        <AddRounded />
                                    </Button>
                                </IconButton>
                            </Stack>
                        </LtrProvider>
                        <Typography
                            variant="bodySmall"
                            fontWeight={500}
                            color="GrayText"
                        >
                            {t('Monthly Emails')}
                        </Typography>
                        <Box px={2} mt={3}>
                            <LtrProvider>
                                <Slider
                                    min={0}
                                    max={periodPrices.length - 1}
                                    value={selectedPriceIndex}
                                    onChange={(_, newValue) =>
                                        handlePriceChange(newValue as number)
                                    }
                                    sx={{
                                        '& .MuiSlider-rail': {
                                            color: 'divider',
                                            height: 8,
                                        },
                                        '& .MuiSlider-mark': {
                                            display: 'none',
                                        },
                                    }}
                                    marks={marks}
                                />
                            </LtrProvider>
                        </Box>
                    </Box>
                    <Card
                        variant="outlined"
                        sx={{
                            p: 1,
                            borderWidth: 2,
                            borderColor: 'secondaryContainer.main',
                            textAlign: 'left',
                            bgcolor: theme =>
                                theme.palette.secondaryContainer.main + '40',
                            '&:hover': {
                                bgcolor: theme =>
                                    theme.palette.secondaryContainer.main +
                                    '40',
                                borderColor: 'secondaryContainer.main',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <CardContent>
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                            >
                                <Box>
                                    <Typography
                                        variant="caption"
                                        textTransform="uppercase"
                                    >
                                        {isYearly && !isNewPlanFree
                                            ? t('Monthly Average')
                                            : t('Monthly Cost')}
                                    </Typography>
                                    <Box
                                        display="flex"
                                        gap={1}
                                        alignItems="baseline"
                                    >
                                        <Typography
                                            variant="headlineLarge"
                                            fontWeight={900}
                                            color="text.primary"
                                        >
                                            {previewLoading ? (
                                                <Skeleton
                                                    variant="text"
                                                    width={100}
                                                />
                                            ) : (
                                                currentTransition.formattedMonthlyCost
                                            )}
                                        </Typography>
                                        <Typography
                                            variant="bodySmall"
                                            color="GrayText"
                                            fontWeight={700}
                                        >
                                            /{t('mo')}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Box textAlign="right">
                                    <Typography
                                        variant="caption"
                                        textTransform="uppercase"
                                    >
                                        {t('Billing')}
                                    </Typography>
                                    {isNewPlanFree ? (
                                        <Typography
                                            variant="bodyMedium"
                                            color="GrayText"
                                            fontWeight={700}
                                        >
                                            {t('Free Forever')}
                                        </Typography>
                                    ) : isYearly ? (
                                        <>
                                            <Typography
                                                variant="bodyMedium"
                                                fontWeight={700}
                                            >
                                                {previewLoading ? (
                                                    <Skeleton
                                                        variant="text"
                                                        width={100}
                                                    />
                                                ) : (
                                                    currentTransition.periodCost
                                                )}
                                            </Typography>
                                            <Typography
                                                variant="bodyMedium"
                                                color="GrayText"
                                                fontWeight={700}
                                            >
                                                {t('Recurring Yearly')}
                                            </Typography>
                                        </>
                                    ) : (
                                        <Typography
                                            variant="bodyMedium"
                                            color="GrayText"
                                            fontWeight={700}
                                        >
                                            {t('Recurring Monthly')}
                                        </Typography>
                                    )}
                                </Box>
                            </Stack>
                            {currentTransition?.updateMessage &&
                                !previewLoading && (
                                    <>
                                        <Divider sx={{ my: 2 }} />
                                        <Typography
                                            variant="caption"
                                            display="flex"
                                            gap={0.5}
                                        >
                                            <InfoOutlined
                                                sx={{ height: 14, width: 14 }}
                                            />
                                            {currentTransition.updateMessage}
                                        </Typography>
                                    </>
                                )}
                        </CardContent>
                    </Card>
                    <Box display="flex" flexDirection="column" gap={2}>
                        <LoadingButton
                            onClick={currentTransition.handleConfirm}
                            size="extraLarge"
                            startIcon={
                                !currentTransition.isCurrentPlan && (
                                    <CreditCardRounded />
                                )
                            }
                            disabled={previewLoading}
                            variant={
                                currentTransition.isCurrentPlan
                                    ? 'tonal'
                                    : 'filled'
                            }
                            loading={isCancelling || isUpdating}
                        >
                            {currentTransition.confirmText}
                        </LoadingButton>
                        <Typography
                            variant="caption"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            textTransform="uppercase"
                            gap={0.5}
                        >
                            <GppGoodOutlined fontSize="small" />
                            {t('Secure Payment')}
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default ManagePlan;
